/* eslint-disable no-inner-declarations */
/**
 * Carousel Component
 * @module patterns/components/banners/asset-hero-banner
 */

import Glide from '@glidejs/glide';

import { parseToObject } from '@root/src/framework/scripts/utils/parse';
import { markElementReady } from '~/framework/helpers/element-ready';
import { scrollIntoView } from '~/framework/utils/scroll';
import { type App } from '~/app';

import { isMobile } from '../../../framework/scripts/helpers/browser/media-query';
import JsController from '../js-controller'

import { CLASS_NAME, OPTIONS } from './constants';

// eslint-disable-next-line import/no-unassigned-import
import './product-gallery.scss';

export default (app: App) => {

    const controller = new JsController(app, 'Product Gallery');
    controller.addMountFunction(() => {
        mountProductGallery(controller);
    })

    return controller.init();
}

export const carousels = new Map<string, Glide>();

export function mountProductGallery( controller: JsController ) {

    // Select all hotspot buttons
    const hotspotContainers = document.querySelectorAll('.hotspot-container');

    for (const container of hotspotContainers) {
        const hotspot = container.querySelector<HTMLElement>('.hotspot');
        const infoBox = container.querySelector('.info-box');
        if (hotspot && !hotspot.dataset.listenerAdded && infoBox) {
            controller.addListener(hotspot, "click", (event) => {
                event.preventDefault();
                event.stopPropagation();

                // Hide all other info boxes except the one related to the clicked hotspot
                for (const box of document.querySelectorAll('.info-box')) {
                    if (box !== infoBox) {
                        box.classList.add('hidden');
                    }
                }

                // Toggle the visibility of the current info box
                infoBox.classList.toggle('hidden');
            });
            // Mark the hotspot as having the event listener added
            hotspot.dataset.listenerAdded = 'true';
        }
    }

    const infoBoxes = document.querySelectorAll('.info-box');

    controller.addListener(document, "click", () =>{
        for (const infoBox of infoBoxes) {
            infoBox.classList.add('hidden')
        }
    })

    for (const infoBox of infoBoxes) {
        controller.addListener(infoBox, "click", (event) =>{
            event.stopPropagation();
        })
    }

    const elements = document.querySelectorAll<HTMLElement>(`.${CLASS_NAME}`)
    const productGallery = document.querySelectorAll<HTMLElement>('.product-gallery')

    //
    const initialiseProductGallery = (elements: NodeListOf<HTMLElement>) => {

        for (const gallery of productGallery) {
            for (const element of elements) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                const carouselOptions = parseToObject<any>(element.dataset.carouselOptions);
                const carouselId = element.dataset.carouselId ?? "";
                const slides = element.querySelectorAll<HTMLElement>('.carousel__slide:not(.glide__slide--clone)')


                const indexClass = `${CLASS_NAME}-${carouselId}`;
                element.classList.add(indexClass);

                const existingGlides = carousels.get(indexClass)!;
                if (existingGlides) {
                    existingGlides.destroy();
                }

                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                const glide = new Glide(`.${indexClass}`, {
                    ...OPTIONS,
                    ...carouselOptions,
                    type: 'carousel',
                    perView: 1,
                    gap: 90,
                });



                const updateNumberIndicatorIndex = (slides: NodeListOf<HTMLElement>) => {
                    const slideIndex = element.querySelector('.slide-index');

                    if (slideIndex && slides) {
                        slideIndex.innerHTML = `${Number(glide.index) + 1} / ${slides.length}`;
                    }
                };

                function initialiseClicks(){
                    const galleryImage = gallery.querySelectorAll<HTMLElement>('.gallery-images');
                    const desktopOnlyGalleryImages = gallery.querySelectorAll<HTMLElement>('.gallery-images.desktop-only')
                    const desktopImageCount = desktopOnlyGalleryImages.length ?? 0

                    // eslint-disable-next-line prefer-const
                    for (let [index, image] of galleryImage.entries()) {
                        if(image.classList.contains("mobile")){
                            index = index-desktopImageCount
                        }

                        refreshCarouselOnClick(image,index)
                    }
                }

                // Fullscreen view
                const modalDialog = gallery.querySelector<HTMLElement>('.modal-dialog')
                const modalContent = gallery.querySelector<HTMLElement>('.modal-content')
                const expandButton = gallery.querySelector<HTMLElement>('.expand-button')
                const thumbnailList = gallery.querySelector<HTMLElement>('.gallery-thumbnail-list')
                const carouselImageContainer = gallery.querySelector<HTMLElement>('.carousel__image-container')
                const closeModalButton = gallery.querySelector<HTMLElement>('.close-modal-button')
                const toggleCssclasses = (element : HTMLElement, ...classes: string[]) => classes.map(className => element.classList.toggle(className));

                const toggleFullscreen = ()=>{
                    if(modalDialog && modalContent && thumbnailList && carouselImageContainer){
                        if(isMobile()){
                            toggleCssclasses(thumbnailList, 'hidden');
                            toggleCssclasses(carouselImageContainer, 'mx-6');
                            toggleCssclasses(modalContent, 'max-h-[720px]');
                            toggleCssclasses(modalDialog, 'fullscreen');
                            glide.update();
                        }else{
                            toggleCssclasses(modalDialog, 'narrow','fullscreen');
                            toggleCssclasses(modalContent, 'md:max-h-[1002px]');
                            toggleCssclasses(modalContent, 'max-h-[720px]');
                            toggleCssclasses(thumbnailList, 'hidden');
                            glide.update();
                        }

                    }
                }

                if(closeModalButton){
                    controller.addListener(closeModalButton,'click', ()=>{
                        if(!isMobile()){
                            modalDialog?.classList.add('narrow')
                            modalDialog?.classList.remove('fullscreen')
                            modalContent?.classList.add('md:max-h-[1002px]')
                            thumbnailList?.classList.remove('hidden')
                            glide.update();
                        }
                    })
                }

                if(expandButton){
                    controller.addListener(expandButton,'click', ()=>{
                        toggleFullscreen()
                    })
                }


                const observer = new MutationObserver((mutations, obs) => {
                    const gallery = document.querySelector('.product-gallery');
                    if (gallery && gallery.querySelectorAll('.gallery-images').length > 0) {
                        initialiseClicks();

                        const modals = document.querySelectorAll<HTMLElement>('.product-gallery-modal')

                        for (const modal of modals){

                            const floorPlanButton = modal.querySelectorAll<HTMLElement>('.floor-plan-button')
                            for (const button of floorPlanButton){
                                goToFloorPlanCarouselOnClick(button)
                            }
                        }

                        obs.disconnect();
                    }
                });

                observer.observe(document, { childList: true, subtree: true });

                const extendedGalleryImage = gallery.querySelectorAll<HTMLElement>('.gallery-images-extended')
                for (const extended of extendedGalleryImage){
                    let index = 6
                    if (extended.classList.contains("mobile")){
                        index = 3
                    }

                    refreshCarouselOnClick(extended,index)
                }

                const expandButtonDetailPage = document.querySelectorAll<HTMLElement>('.product-detail-zoom-button, .floorplan-button')
                for (const detailButton of expandButtonDetailPage) {
                    goToFloorPlanCarouselOnClick(detailButton)
                }

                function refreshCarouselOnClick(element: HTMLElement,slideStart?:number) {
                    controller.addListener(element, 'click', ()=>{
                        setTimeout(() => {
                            glide.update(
                                {
                                    startAt:slideStart ?? 0
                                }
                            );
                        }, 200);
                    })
                }

                function goToFloorPlanCarouselOnClick(element: HTMLElement) {
                    controller.addListener(element, 'click', ()=>{
                        setTimeout(() => {
                            // Ensure the carousel is ready
                            // glide.update({
                            //     startAt: 0
                            // })
                            const galleryCarouselSlides = gallery.querySelectorAll<HTMLElement>('.gallery-carousel-slide');
                            // eslint-disable-next-line prefer-const
                            for (let [index, image] of galleryCarouselSlides.entries()) {
                                if(image.classList.contains("floor-plan") && !image.classList.contains("glide__slide--clone")){
                                    // glide.go(`=${index}`);
                                    glide.update({
                                        // Exclude first 2 clone slide
                                        startAt: index - 2
                                    })
                                    return;
                                }
                            }

                            glide.update({
                                startAt: 0
                            })
                        }, 200);
                    })
                }

                const scrollImageIntoView = (element:HTMLElement) => {
                    scrollIntoView(element, {
                        behavior: 'smooth',
                        inline: 'center',
                        block: 'nearest'
                    });
                };

                const thumbnailObserver = new MutationObserver((mutations, obs) => {

                    if( gallery && gallery?.querySelectorAll<HTMLElement>('.gallery-thumbnail-list > div').length > 0 ) {
                        // Observe class change in image thumbnails
                        const galleryPictures = gallery?.querySelectorAll<HTMLElement>('.gallery-thumbnail-list > div')
                        const galleryImageDescription = gallery.querySelectorAll<HTMLElement>('.image-description');
                        const slideIndex = gallery.querySelector('.slide-index');

                        if (galleryPictures) for (const picture of galleryPictures) {
                            const observer = new MutationObserver(function() {
                                if(picture.classList.contains('glide__bullet--active')){
                                    scrollImageIntoView(picture)
                                    for (const description of galleryImageDescription) {

                                        if (description.dataset.imageIndex === picture?.dataset.imageIndex){
                                            description?.classList.remove('hidden')
                                        }else if(!description.classList.contains('hidden')){
                                            description?.classList.add('hidden')
                                        }
                                    }

                                    if(picture.classList.contains('video-thumbnail')){
                                        slideIndex?.classList.add('hidden')
                                    }else{
                                        slideIndex?.classList.remove('hidden')

                                    }
                                }
                            });
                            observer.observe(picture, {
                                attributes: true,
                                attributeFilter: ["class"]
                            });
                        }

                        // Optionally disconnect the observer if no longer needed
                        obs.disconnect();
                    }
                });

                thumbnailObserver.observe(document, { childList: true, subtree: true });



                glide.on('update', function () {
                    updateNumberIndicatorIndex(slides);
                });

                glide.on('swipe.end', function () {
                    updateNumberIndicatorIndex(slides);
                });

                glide.on('move.after', function () {
                    updateNumberIndicatorIndex(slides);
                });

                // On screensize re-initialise the carousel to handle per view updates on different screensizes
                const glideUpdate = () => glide.update();

                controller.addListener(window, "resize", glideUpdate)

                glide.mount();

                carousels.set(indexClass, glide);

                markElementReady(element);

            }
        }
    };


    initialiseProductGallery(elements);

    const modal = document.querySelector<HTMLElement>('#media-gallery-modal-id')
    if (modal && !modal.dataset.listenerAdded) {
        controller.addListener(modal, 'hidden.bs.modal', () => {
            const allVideoControls = document.querySelectorAll('.plyr__control');
            for (const button of allVideoControls) {
                if (button.classList.contains('plyr__control--pressed')) {
                    button.dispatchEvent(new Event('click'));
                }
            }
        });
        modal.dataset.listenerAdded = 'true';
    }

}


